import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';
import { RootState } from './store';

export interface EdlElement {
  id: number;
  name: string;
  room_category: string; // 'CUISINE' | 'SALLE_DE_BAINS' | 'CHAUFFAGE' | custom
  state: string;         // '1'=BE '2'=EM '3'=ME ''=non renseigné
  observations: string;
  LocalId: number;
}

interface EdlElementState {
  elements: EdlElement[];
  loading: boolean;
  error: string | null;
}

const initialState: EdlElementState = {
  elements: [],
  loading: false,
  error: null,
};

const getToken = (state: RootState) => (state as any).authentification?.user?.token;

export const fetchEdlElementsByLocal = createAsyncThunk<EdlElement[], number, { state: RootState }>(
  'edlElement/fetchByLocal',
  async (localId, { getState, rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseUrlApi}/edlelements/local/${localId}`, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch EDL elements');
    }
  }
);

export const initEdlElementDefaults = createAsyncThunk<EdlElement[], number, { state: RootState }>(
  'edlElement/initDefaults',
  async (localId, { getState, rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrlApi}/edlelements/local/${localId}/init`, {}, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to init defaults');
    }
  }
);

export const createEdlElement = createAsyncThunk<EdlElement, Partial<EdlElement>, { state: RootState }>(
  'edlElement/create',
  async (data, { getState, dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrlApi}/edlelements`, data, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      if (data.LocalId) dispatch(fetchEdlElementsByLocal(data.LocalId));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create element');
    }
  }
);

export const updateEdlElement = createAsyncThunk<
  EdlElement,
  { id: number; data: Partial<EdlElement>; localId: number },
  { state: RootState }
>(
  'edlElement/update',
  async ({ id, data, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const res = await axios.put(`${baseUrlApi}/edlelements/${id}`, data, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      dispatch(fetchEdlElementsByLocal(localId));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update element');
    }
  }
);

export const bulkUpdateEdlElements = createAsyncThunk<
  void,
  { items: Array<{ id: number; state: string; observations: string }>; localId: number },
  { state: RootState }
>(
  'edlElement/bulkUpdate',
  async ({ items, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      await axios.put(`${baseUrlApi}/edlelements/bulk`, { items }, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      dispatch(fetchEdlElementsByLocal(localId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to bulk update');
    }
  }
);

export const deleteEdlElement = createAsyncThunk<
  void,
  { id: number; localId: number },
  { state: RootState }
>(
  'edlElement/delete',
  async ({ id, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrlApi}/edlelements/${id}`, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      dispatch(fetchEdlElementsByLocal(localId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete element');
    }
  }
);

const edlElementSlice = createSlice({
  name: 'edlElement',
  initialState,
  reducers: {
    clearEdlElements: (state) => { state.elements = []; },
  },
  extraReducers: (builder) => {
    const pending  = (state: EdlElementState) => { state.loading = true; state.error = null; };
    const rejected = (state: EdlElementState, action: any) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchEdlElementsByLocal.pending,  pending)
      .addCase(fetchEdlElementsByLocal.rejected, rejected)
      .addCase(fetchEdlElementsByLocal.fulfilled, (state, action) => {
        state.loading  = false;
        state.elements = action.payload;
      })
      .addCase(initEdlElementDefaults.pending,  pending)
      .addCase(initEdlElementDefaults.rejected, rejected)
      .addCase(initEdlElementDefaults.fulfilled, (state, action) => {
        state.loading  = false;
        state.elements = action.payload;
      });
  },
});

export const { clearEdlElements } = edlElementSlice.actions;
export default edlElementSlice.reducer;
